// src/components/course/manager/LessonEditor.jsx
import React, { useState, useEffect } from 'react';
import {
  Save, ArrowLeft, Upload, Play, FileText, HelpCircle, Image as ImageIcon,
  Clock, Eye, Plus, X, CheckCircle, GripVertical, Trash2, Edit3, Target,
  Brain, MessageSquare, Star, Square
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import { firestoreService } from '../../../services/firestoreService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const LessonEditor = ({ bookId, courseId, lessonId, user, onNavigate }) => {
  const isEditing = !!lessonId;
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'video',
    duration: 0,
    isPreview: false
  });

  const [contentBlocks, setContentBlocks] = useState([]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadData();
  }, [courseId, lessonId, bookId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load course
      const courseData = await courseService.getCourse(bookId, courseId);
      setCourse(courseData);

      // Load lesson if editing
      if (isEditing) {
        const lessonData = await courseService.getLesson(bookId, courseId, lessonId);
        if (lessonData) {
          setLesson(lessonData);
          setFormData({
            title: lessonData.title || '',
            description: lessonData.description || '',
            type: lessonData.type || 'video',
            duration: lessonData.duration || 0,
            isPreview: lessonData.isPreview || false
          });
          setContentBlocks(lessonData.contentBlocks || []);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load lesson data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    

    if (!courseId) {
      alert('Course ID is missing');
      return;
    }

    try {
      setSaving(true);

      const lessonData = {
        ...formData,
        // REMOVE courseId from here - it's passed as parameter
        creatorId: user.uid,
        duration: parseInt(formData.duration),
        contentBlocks: contentBlocks.map((block, index) => ({
          ...block,
          order: index
        })),
        order: isEditing ? lesson.order : await getNextLessonOrder(),
        updatedAt: new Date()
      };

      if (isEditing) {
        await courseService.updateLesson(bookId, courseId, lessonId, lessonData);
        alert('Lesson updated successfully!');
      } else {
        const newLessonId = await courseService.createLesson(bookId, courseId, lessonData);
        alert('Lesson created successfully!');
        onNavigate('creator-detail', { courseId });
        return;
      }

      onNavigate('creator-detail', { courseId });

    } catch (error) {
      console.error('Error saving lesson:', error);
      alert('Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  const getNextLessonOrder = async () => {
    try {
      const lessons = await courseService.getLessons(bookId, courseId);
      return lessons.length;
    } catch (error) {
      return 0;
    }
  };

  // Content Block Management
  const addContentBlock = (type) => {
    const newBlock = {
      id: `block_${Date.now()}`,
      type,
      title: '',
      content: '',
      instructions: '',
      order: contentBlocks.length
    };

    switch (type) {
      case 'video':
        newBlock.title = 'Video Content';
        newBlock.videoUrl = '';
        newBlock.videoFileUrl = '';
        break;
      case 'text':
        newBlock.title = 'Text Content';
        newBlock.content = '';
        break;
      case 'quiz':
        newBlock.title = 'Knowledge Check';
        newBlock.quizQuestions = [{
          question: '',
          options: ['', '', '', ''],
          correctAnswer: 0,
          explanation: ''
        }];
        break;
      case 'assessment':
        newBlock.title = 'Interactive Assessment';
        newBlock.questions = [{
          type: 'multiple-choice',
          question: '',
          options: ['', '', '', ''],
          correctAnswer: 0,
          explanation: ''
        }];
        break;
      case 'image':
        newBlock.title = 'Image/Infographic';
        newBlock.imageUrl = '';
        newBlock.imageFileUrl = '';
        break;
    }

    setContentBlocks([...contentBlocks, newBlock]);
  };

  const updateContentBlock = (blockId, updates) => {
    setContentBlocks(blocks =>
      blocks.map(block =>
        block.id === blockId ? { ...block, ...updates } : block
      )
    );
  };

  const removeContentBlock = (blockId) => {
    if (contentBlocks.length === 1) {
      alert('A lesson must have at least one content block');
      return;
    }
    setContentBlocks(blocks => blocks.filter(block => block.id !== blockId));
  };

  const duplicateContentBlock = (blockId) => {
    const blockToDuplicate = contentBlocks.find(block => block.id === blockId);
    if (blockToDuplicate) {
      const duplicatedBlock = {
        ...blockToDuplicate,
        id: `block_${Date.now()}`,
        title: `${blockToDuplicate.title} (Copy)`
      };
      const blockIndex = contentBlocks.findIndex(block => block.id === blockId);
      const newBlocks = [...contentBlocks];
      newBlocks.splice(blockIndex + 1, 0, duplicatedBlock);
      setContentBlocks(newBlocks);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setContentBlocks((blocks) => {
        const oldIndex = blocks.findIndex(block => block.id === active.id);
        const newIndex = blocks.findIndex(block => block.id === over.id);
        return arrayMove(blocks, oldIndex, newIndex);
      });
    }
  };

  const handleFileUpload = async (file, type, blockId) => {
    try {
      setUploadProgress(10);
      const uploadPath = `${bookId}/courses/${courseId}/lessons/${lessonId || 'new'}/${type}_${blockId}_${Date.now()}`;
      
      const downloadURL = await firestoreService.uploadFile(file, uploadPath, (progress) => {
        setUploadProgress(progress);
      });
      
      updateContentBlock(blockId, { [`${type}FileUrl`]: downloadURL });
      alert('File uploaded successfully!');
      setUploadProgress(0);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
      setUploadProgress(0);
    }
  };

  const contentBlockTypes = [
    { type: 'video', label: 'Video', icon: Play, description: 'Video content from URL or upload' },
    { type: 'text', label: 'Text/Article', icon: FileText, description: 'Rich text content with markdown' },
    { type: 'image', label: 'Image', icon: ImageIcon, description: 'Images and infographics' },
    { type: 'quiz', label: 'Quick Quiz', icon: HelpCircle, description: 'Simple multiple choice questions' },
    { type: 'assessment', label: 'Interactive Assessment', icon: Target, description: 'Advanced assessments with drag-drop, matching, etc.' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lesson...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => onNavigate('creator-detail', { courseId })}
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit Lesson' : 'Create New Lesson'}
              </h1>
              <p className="text-gray-600">
                {course?.title} • {isEditing ? 'Update lesson content' : 'Add content and interactive elements'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <Eye className="w-4 h-4" />
              <span>{showPreview ? 'Hide Preview' : 'Preview'}</span>
            </button>

            <button
              form="lesson-form"
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save className="w-5 h-5" />
              )}
              <span>{saving ? 'Saving...' : (isEditing ? 'Update Lesson' : 'Create Lesson')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form id="lesson-form" onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Lesson Details</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lesson Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPreview}
                      onChange={(e) => setFormData({ ...formData, isPreview: e.target.checked })}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Available as preview
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Content Blocks */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Lesson Content</h3>
              <span className="text-sm text-gray-500">
                {contentBlocks.length} block{contentBlocks.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Block Type Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {contentBlockTypes.map((type) => (
                <button
                  key={type.type}
                  type="button"
                  onClick={() => addContentBlock(type.type)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left group"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <type.icon className="w-6 h-6 text-blue-600 group-hover:text-blue-700" />
                    <h4 className="font-medium text-gray-900">{type.label}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{type.description}</p>
                </button>
              ))}
            </div>

            {/* Content Blocks List */}
            {contentBlocks.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={contentBlocks.map(b => b.id)} 
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-6">
                    {contentBlocks.map((block, index) => (
                      <ContentBlock
                        key={block.id}
                        block={block}
                        index={index}
                        onUpdate={updateContentBlock}
                        onRemove={removeContentBlock}
                        onDuplicate={duplicateContentBlock}
                        onFileUpload={handleFileUpload}
                        uploadProgress={uploadProgress}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Plus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">No content blocks yet</h4>
                <p className="text-gray-600">Add content blocks above to build your lesson</p>
              </div>
            )}
          </div>

          {/* Interactive Features Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Brain className="w-6 h-6 text-blue-600 mt-1" />
              <div>
                <h3 className="font-medium text-blue-900 mb-2">Interactive Learning Features</h3>
                <p className="text-blue-800 text-sm mb-3">
                  This lesson will automatically include interactive elements for learners:
                </p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-center space-x-2">
                    <Star className="w-4 h-4" />
                    <span>Understanding ratings for each content block</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>Note-taking and question submission</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Target className="w-4 h-4" />
                    <span>Interactive assessments with drag-drop and matching</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Progress tracking and completion certificates</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Content Block Component with drag-and-drop
const ContentBlock = ({ block, index, onUpdate, onRemove, onDuplicate, onFileUpload, uploadProgress }) => {
  const [collapsed, setCollapsed] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const updateBlock = (field, value) => {
    onUpdate(block.id, { [field]: value });
  };

  const getBlockIcon = (type) => {
    switch (type) {
      case 'video': return Play;
      case 'text': return FileText;
      case 'image': return ImageIcon;
      case 'quiz': return HelpCircle;
      case 'assessment': return Target;
      default: return Square;
    }
  };

  const BlockIcon = getBlockIcon(block.type);

  const renderBlockEditor = () => {
    switch (block.type) {
      case 'video':
        return <VideoBlockEditor block={block} onUpdate={updateBlock} onFileUpload={onFileUpload} />;
      case 'text':
        return <TextBlockEditor block={block} onUpdate={updateBlock} />;
      case 'image':
        return <ImageBlockEditor block={block} onUpdate={updateBlock} onFileUpload={onFileUpload} />;
      case 'quiz':
        return <QuizBlockEditor block={block} onUpdate={onUpdate} />;
      case 'assessment':
        return <AssessmentBlockEditor block={block} onUpdate={onUpdate} />;
      default:
        return <div className="text-center py-4 text-gray-500">Unknown block type</div>;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-gray-200 rounded-xl overflow-hidden"
    >
      {/* Block Header */}
      <div className="bg-gray-50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              {...attributes}
              {...listeners}
              className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            >
              <GripVertical className="w-5 h-5" />
            </div>
            
            <BlockIcon className="w-5 h-5 text-blue-600" />
            
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Block {index + 1}</span>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-xs text-gray-500 capitalize">{block.type}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => onDuplicate(block.id)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Duplicate block"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {collapsed ? <Plus className="w-4 h-4" /> : <X className="w-4 h-4" />}
            </button>

            <button
              type="button"
              onClick={() => onRemove(block.id)}
              className="p-2 text-red-400 hover:text-red-600 transition-colors"
              title="Remove block"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Block Content */}
      {!collapsed && (
        <div className="p-6">
          {renderBlockEditor()}
        </div>
      )}
    </div>
  );
};

// Video Block Editor
const VideoBlockEditor = ({ block, onUpdate, onFileUpload }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Block Title</label>
        <input
          type="text"
          value={block.title || ''}
          onChange={(e) => onUpdate('title', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="e.g., Introduction Video"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube, Vimeo)</label>
        <input
          type="url"
          value={block.videoUrl || ''}
          onChange={(e) => onUpdate('videoUrl', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="https://www.youtube.com/watch?v=..."
        />
      </div>

      <div className="relative">
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-2">Or upload video file</p>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => {
              if (e.target.files[0]) {
                onFileUpload(e.target.files[0], 'video', block.id);
              }
            }}
            className="hidden"
            id={`video-upload-${block.id}`}
          />
          <label
            htmlFor={`video-upload-${block.id}`}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer"
          >
            <Upload className="w-4 h-4 mr-2" />
            Choose File
          </label>
          {block.videoFileUrl && (
            <p className="text-sm text-green-600 mt-2">✓ Video uploaded</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Instructions (optional)</label>
        <textarea
          value={block.instructions || ''}
          onChange={(e) => onUpdate('instructions', e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="Any instructions for the learner..."
        />
      </div>
    </div>
  );
};

// Text Block Editor
const TextBlockEditor = ({ block, onUpdate }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Block Title</label>
        <input
          type="text"
          value={block.title || ''}
          onChange={(e) => onUpdate('title', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="e.g., Key Concepts"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Content (Markdown supported)</label>
        <textarea
          value={block.content || ''}
          onChange={(e) => onUpdate('content', e.target.value)}
          rows={12}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm"
          placeholder="Write your content here... Markdown is supported"
        />
        <p className="text-xs text-gray-500 mt-1">
          Supports **bold**, *italic*, # headings, - lists, etc.
        </p>
      </div>
    </div>
  );
};

// Image Block Editor
const ImageBlockEditor = ({ block, onUpdate, onFileUpload }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Block Title</label>
        <input
          type="text"
          value={block.title || ''}
          onChange={(e) => onUpdate('title', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="e.g., Diagram Overview"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
        <input
          type="url"
          value={block.imageUrl || ''}
          onChange={(e) => onUpdate('imageUrl', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="https://example.com/image.jpg"
        />
      </div>

      <div className="relative">
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-2">Or upload image file</p>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files[0]) {
                onFileUpload(e.target.files[0], 'image', block.id);
              }
            }}
            className="hidden"
            id={`image-upload-${block.id}`}
          />
          <label
            htmlFor={`image-upload-${block.id}`}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer"
          >
            <Upload className="w-4 h-4 mr-2" />
            Choose File
          </label>
          {block.imageFileUrl && (
            <p className="text-sm text-green-600 mt-2">✓ Image uploaded</p>
          )}
        </div>
      </div>

      {(block.imageUrl || block.imageFileUrl) && (
        <div className="mt-4">
          <img
            src={block.imageFileUrl || block.imageUrl}
            alt="Preview"
            className="max-w-full h-auto rounded-lg border border-gray-200"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Caption (optional)</label>
        <textarea
          value={block.content || ''}
          onChange={(e) => onUpdate('content', e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="Add a caption or description..."
        />
      </div>
    </div>
  );
};

// Quiz Block Editor
const QuizBlockEditor = ({ block, onUpdate }) => {
  const addQuestion = () => {
    const newQuestions = [...(block.quizQuestions || []), {
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: ''
    }];
    onUpdate(block.id, { quizQuestions: newQuestions });
  };

  const updateQuestion = (questionIndex, field, value) => {
    const newQuestions = [...(block.quizQuestions || [])];
    newQuestions[questionIndex] = { ...newQuestions[questionIndex], [field]: value };
    onUpdate(block.id, { quizQuestions: newQuestions });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const newQuestions = [...(block.quizQuestions || [])];
    const newOptions = [...newQuestions[questionIndex].options];
    newOptions[optionIndex] = value;
    newQuestions[questionIndex] = { ...newQuestions[questionIndex], options: newOptions };
    onUpdate(block.id, { quizQuestions: newQuestions });
  };

  const removeQuestion = (questionIndex) => {
    const newQuestions = (block.quizQuestions || []).filter((_, index) => index !== questionIndex);
    onUpdate(block.id, { quizQuestions: newQuestions });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Quiz Questions</h4>
        <button
          type="button"
          onClick={addQuestion}
          className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          <span>Add Question</span>
        </button>
      </div>

      {(block.quizQuestions || []).map((question, questionIndex) => (
        <div key={questionIndex} className="p-4 bg-gray-50 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Question {questionIndex + 1}</span>
            <button
              type="button"
              onClick={() => removeQuestion(questionIndex)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <input
            type="text"
            value={question.question}
            onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="Enter your question"
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Options (select correct answer)</label>
            {question.options.map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={`correct-${questionIndex}`}
                  checked={question.correctAnswer === optionIndex}
                  onChange={() => updateQuestion(questionIndex, 'correctAnswer', optionIndex)}
                  className="text-blue-600"
                />
                <input
                  type="text"
                  value={option}
                  onChange={(e) => updateOption(questionIndex, optionIndex, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder={`Option ${optionIndex + 1}`}
                />
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Explanation (optional)</label>
            <textarea
              value={question.explanation || ''}
              onChange={(e) => updateQuestion(questionIndex, 'explanation', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="Explain the correct answer"
            />
          </div>
        </div>
      ))}

      {(!block.quizQuestions || block.quizQuestions.length === 0) && (
        <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <HelpCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No quiz questions yet. Add your first question above.</p>
        </div>
      )}
    </div>
  );
};

// Assessment Block Editor
const AssessmentBlockEditor = ({ block, onUpdate }) => {
  const addQuestion = (type = 'multiple-choice') => {
    const baseQuestion = {
      type,
      question: '',
      explanation: ''
    };

    let newQuestion;
    switch (type) {
      case 'multiple-choice':
        newQuestion = {
          ...baseQuestion,
          options: ['', '', '', ''],
          correctAnswer: 0
        };
        break;
      case 'drag-drop':
        newQuestion = {
          ...baseQuestion,
          instruction: 'Arrange these items in the correct order:',
          items: [
            { id: 'item1', text: 'First item' },
            { id: 'item2', text: 'Second item' },
            { id: 'item3', text: 'Third item' }
          ],
          correctOrder: ['item1', 'item2', 'item3']
        };
        break;
      case 'matching':
        newQuestion = {
          ...baseQuestion,
          instruction: 'Match the items:',
          leftItems: ['Item A', 'Item B', 'Item C'],
          rightItems: ['Match 1', 'Match 2', 'Match 3'],
          correctMatches: { 0: 0, 1: 1, 2: 2 }
        };
        break;
    }

    const newQuestions = [...(block.questions || []), newQuestion];
    onUpdate(block.id, { questions: newQuestions });
  };

  const removeQuestion = (questionIndex) => {
    const newQuestions = (block.questions || []).filter((_, index) => index !== questionIndex);
    onUpdate(block.id, { questions: newQuestions });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Assessment Questions</h4>
        <div className="flex items-center space-x-2">
          <select
            onChange={(e) => {
              addQuestion(e.target.value);
              e.target.value = '';
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            defaultValue=""
          >
            <option value="" disabled>Add Question Type</option>
            <option value="multiple-choice">Multiple Choice</option>
            <option value="drag-drop">Drag & Drop</option>
            <option value="matching">Matching</option>
          </select>
        </div>
      </div>

      {(block.questions || []).map((question, questionIndex) => (
        <div key={questionIndex} className="p-4 bg-gray-50 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Question {questionIndex + 1}</span>
              <span className="text-xs text-gray-500 capitalize">({question.type})</span>
            </div>
            <button
              type="button"
              onClick={() => removeQuestion(questionIndex)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Question type specific editors would go here */}
          <textarea
            value={question.question}
            onChange={(e) => {
              const newQuestions = [...(block.questions || [])];
              newQuestions[questionIndex] = { ...newQuestions[questionIndex], question: e.target.value };
              onUpdate(block.id, { questions: newQuestions });
            }}
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="Enter your question"
          />
        </div>
      ))}

      {(!block.questions || block.questions.length === 0) && (
        <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Target className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No assessment questions yet. Add questions above.</p>
        </div>
      )}
    </div>
  );
};

export default LessonEditor;